import { useEffect, useState } from "react";
import { Box, CircularProgress, Fade } from "@mui/material";

import logo from "../assets/logos/PROCare_logo.svg";

/**
 * Page-level loading state: the session bootstrap, a pending detail query, a
 * lazy route chunk.
 *
 * Renders NOTHING for the first `delayMs`. Almost everything it covers
 * resolves well inside that, and a logo that appears and disappears within
 * 200ms reads as a glitch — showing nothing reads as instant. Only a genuinely
 * slow load gets a splash, and the spinner fades in later still, so a merely
 * sluggish one shows the logo alone.
 *
 * Pass `delayMs={0}` where the wait is known to be long and an immediate
 * placeholder is better than a blank page.
 */
export function SplashScreen({ delayMs = 200 }: { delayMs?: number }) {
  const [visible, setVisible] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  if (!visible) return null;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        gap: 3,
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ display: "grid", justifyItems: "center", gap: 3 }}>
        <Box component="img" src={logo} alt="ProCare" sx={{ height: 36 }} />
        <Fade in style={{ transitionDelay: "150ms" }} unmountOnExit>
          <CircularProgress size={22} thickness={5} aria-label="" />
        </Fade>
      </Box>
    </Box>
  );
}
